import { makeRegimeFeatures, standardizeRegimeFeatures } from "./hmmRegimes";

const STATES = ["range", "trend", "highVol"];
const CALIBRATION_FEATURES = 200;
const VARIANCE_FLOOR = 0.04;
const MAX_GMM_ITERATIONS = 30;
const MAX_BOCPD_HYPOTHESES = 300;

const clampLookback = (value) => Math.max(2, Math.min(500, Math.floor(Number(value) || 20)));
const clampThreshold = (value, fallback) => Math.max(0.01, Math.min(0.99, Number(value) || fallback));

function logSumExp(values) {
  const maximum = Math.max(...values);
  if (!Number.isFinite(maximum)) return maximum;
  return maximum + Math.log(values.reduce((sum, value) => sum + Math.exp(value - maximum), 0));
}

function componentLogLikelihood(point, means, variances, weight) {
  let score = Math.log(Math.max(weight, 1e-12));
  for (let dimension = 0; dimension < 2; dimension += 1) {
    const variance = Math.max(VARIANCE_FLOOR, variances[dimension]);
    const delta = point[dimension] - means[dimension];
    score += -0.5 * (Math.log(2 * Math.PI * variance) + (delta * delta) / variance);
  }
  return score;
}

function posteriorForPoint(point, model) {
  const scores = model.means.map((means, index) => componentLogLikelihood(
    point,
    means,
    model.variances[index],
    model.weights[index]
  ));
  const normalizer = logSumExp(scores);
  return scores.map((score) => Math.exp(score - normalizer));
}

function fitGaussianMixture(observations) {
  if (observations.length < CALIBRATION_FEATURES) {
    throw new Error(`GMM needs ${CALIBRATION_FEATURES} calibration feature bars.`);
  }
  const squaredDistance = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
  const centers = [observations[0]];
  while (centers.length < 3) {
    let furthest = observations[0];
    let distance = -1;
    observations.forEach((point) => {
      const nearest = Math.min(...centers.map((center) => squaredDistance(point, center)));
      if (nearest > distance) {
        furthest = point;
        distance = nearest;
      }
    });
    centers.push(furthest);
  }
  const model = {
    means: centers.map((point) => [...point]),
    variances: centers.map(() => [1, 1]),
    weights: [1 / 3, 1 / 3, 1 / 3],
    effectiveWeights: [0, 0, 0],
  };

  for (let iteration = 0; iteration < MAX_GMM_ITERATIONS; iteration += 1) {
    const responsibilities = observations.map((point) => posteriorForPoint(point, model));
    const effectiveWeights = [0, 0, 0];
    const means = Array.from({ length: 3 }, () => [0, 0]);
    responsibilities.forEach((row, index) => {
      for (let component = 0; component < 3; component += 1) {
        const weight = row[component];
        effectiveWeights[component] += weight;
        means[component][0] += weight * observations[index][0];
        means[component][1] += weight * observations[index][1];
      }
    });
    for (let component = 0; component < 3; component += 1) {
      const weight = Math.max(effectiveWeights[component], 1e-8);
      means[component] = means[component].map((value) => value / weight);
    }
    const variances = Array.from({ length: 3 }, () => [0, 0]);
    responsibilities.forEach((row, index) => {
      for (let component = 0; component < 3; component += 1) {
        for (let dimension = 0; dimension < 2; dimension += 1) {
          variances[component][dimension] += row[component] * (observations[index][dimension] - means[component][dimension]) ** 2;
        }
      }
    });
    model.means = means;
    model.variances = variances.map((row, component) => row.map((value) => Math.max(VARIANCE_FLOOR, value / Math.max(effectiveWeights[component], 1e-8))));
    model.weights = effectiveWeights.map((weight) => weight / observations.length);
    model.effectiveWeights = effectiveWeights;
  }
  if (model.effectiveWeights.some((weight) => weight < 2)) {
    throw new Error("The calibration data did not support three distinct GMM clusters.");
  }
  return model;
}

function labelMixtureStates(model) {
  const ids = [0, 1, 2];
  const highVolId = ids.reduce((best, id) => model.means[id][0] > model.means[best][0] ? id : best, 0);
  const remaining = ids.filter((id) => id !== highVolId);
  const trendId = model.means[remaining[0]][1] >= model.means[remaining[1]][1] ? remaining[0] : remaining[1];
  const rangeId = remaining.find((id) => id !== trendId);
  return { [rangeId]: "range", [trendId]: "trend", [highVolId]: "highVol" };
}

/** A causal, static GMM: fit once to the first 200 feature observations, then classify forward. */
export function detectGMMRegimes(candles, { lookback = 20, calibrationFeatures = CALIBRATION_FEATURES } = {}) {
  const features = makeRegimeFeatures(candles, clampLookback(lookback));
  const calibrationCount = Math.max(CALIBRATION_FEATURES, Math.floor(Number(calibrationFeatures) || CALIBRATION_FEATURES));
  if (features.length <= calibrationCount) {
    return { status: "insufficient", reason: `Load more candles than the ${calibrationCount}-feature calibration window.`, bars: [], calibrationFeatures: calibrationCount };
  }
  try {
    const calibration = features.slice(0, calibrationCount);
    const normalized = standardizeRegimeFeatures(calibration, features).values;
    const model = fitGaussianMixture(normalized.slice(0, calibrationCount));
    const labels = labelMixtureStates(model);
    const bars = normalized.slice(calibrationCount).map((point, index) => {
      const probabilities = posteriorForPoint(point, model);
      let cluster = 0;
      if (probabilities[1] > probabilities[cluster]) cluster = 1;
      if (probabilities[2] > probabilities[cluster]) cluster = 2;
      const namedProbabilities = {
        [labels[0]]: probabilities[0],
        [labels[1]]: probabilities[1],
        [labels[2]]: probabilities[2],
      };
      return {
        time: features[calibrationCount + index].time,
        kind: labels[cluster],
        phase: "causal",
        probabilities: namedProbabilities,
        confidence: Math.max(...probabilities),
      };
    });
    return { status: "ready", reason: "", bars, lookback: clampLookback(lookback), calibrationFeatures: calibrationCount };
  } catch (error) {
    return { status: "unstable", reason: error?.message || "The GMM could not be fitted to these features.", bars: [], calibrationFeatures: calibrationCount };
  }
}

function normalLogDensity(value, mean, variance) {
  const safeVariance = Math.max(1e-8, variance);
  return -0.5 * (Math.log(2 * Math.PI * safeVariance) + ((value - mean) ** 2) / safeVariance);
}

/**
 * A causal BOCPD alarm over rolling log-volatility. The observation variance is
 * calibrated on the first 200 feature bars, the Gaussian mean may reset at a
 * change, and the geometric hazard is set by expectedRunLength.
 */
export function detectBOCPDRegimes(candles, {
  lookback = 20,
  expectedRunLength = 100,
  threshold = 0.25,
  calibrationFeatures = CALIBRATION_FEATURES,
  maxHypotheses = MAX_BOCPD_HYPOTHESES,
} = {}) {
  const period = clampLookback(lookback);
  const calibrationCount = Math.max(CALIBRATION_FEATURES, Math.floor(Number(calibrationFeatures) || CALIBRATION_FEATURES));
  const runLengthPrior = Math.max(10, Math.min(5000, Math.floor(Number(expectedRunLength) || 100)));
  const alarmThreshold = clampThreshold(threshold, 0.25);
  const features = makeRegimeFeatures(candles, period);
  if (features.length <= calibrationCount) {
    return { status: "insufficient", reason: `Load more candles than the ${calibrationCount}-feature calibration window.`, bars: [], calibrationFeatures: calibrationCount };
  }

  const normalized = standardizeRegimeFeatures(features.slice(0, calibrationCount), features).values;
  const observationVariance = 1;
  const priorVariance = 1;
  const hazard = 1 / runLengthPrior;
  let hypotheses = [{ runLength: 0, probability: 1, count: 0, sum: 0 }];
  const bars = [];
  const limit = Math.max(20, Math.min(1000, Math.floor(Number(maxHypotheses) || MAX_BOCPD_HYPOTHESES)));

  for (let index = calibrationCount; index < normalized.length; index += 1) {
    const observation = normalized[index][0];
    const resetLikelihood = normalLogDensity(observation, 0, observationVariance + priorVariance);
    const resetTerms = [];
    const growthTerms = [];
    for (const hypothesis of hypotheses) {
      const posteriorVariance = 1 / (1 / priorVariance + hypothesis.count / observationVariance);
      const posteriorMean = posteriorVariance * hypothesis.sum / observationVariance;
      const predictiveVariance = observationVariance + posteriorVariance;
      const logPrior = Math.log(Math.max(hypothesis.probability, 1e-300));
      resetTerms.push(logPrior + Math.log(hazard) + resetLikelihood);
      growthTerms.push({
        runLength: hypothesis.runLength + 1,
        logMass: logPrior + Math.log(1 - hazard) + normalLogDensity(observation, posteriorMean, predictiveVariance),
        count: hypothesis.count + 1,
        sum: hypothesis.sum + observation,
      });
    }
    const resetLogMass = logSumExp(resetTerms);
    const totalLogMass = logSumExp([...resetTerms, ...growthTerms.map((term) => term.logMass)]);
    if (!Number.isFinite(totalLogMass)) {
      return { status: "error", reason: "BOCPD probabilities became numerically unstable.", bars: [], calibrationFeatures: calibrationCount };
    }
    const next = growthTerms.map(({ logMass, ...hypothesis }) => ({
      ...hypothesis,
      probability: Math.exp(logMass - totalLogMass),
    }));
    next.push({ runLength: 0, probability: Math.exp(resetLogMass - totalLogMass), count: 1, sum: observation });
    next.sort((a, b) => b.probability - a.probability);
    hypotheses = next.slice(0, limit);
    const retainedMass = hypotheses.reduce((sum, hypothesis) => sum + hypothesis.probability, 0) || 1;
    hypotheses.forEach((hypothesis) => { hypothesis.probability /= retainedMass; });
    const changeProbability = Math.exp(resetLogMass - totalLogMass);
    bars.push({
      time: features[index].time,
      kind: changeProbability >= alarmThreshold ? "change" : "quiet",
      phase: "causal",
      changeProbability,
      runLength: hypotheses.reduce((sum, hypothesis) => sum + hypothesis.runLength * hypothesis.probability, 0),
    });
  }

  return {
    status: "ready",
    reason: "",
    bars,
    lookback: period,
    calibrationFeatures: calibrationCount,
    expectedRunLength: runLengthPrior,
    threshold: alarmThreshold,
  };
}

export const REGIME_COMPARISON_OPTIONS = Object.freeze([
  { value: "hmm", label: "HMM · persistent states" },
  { value: "gmm", label: "GMM · static clusters" },
  { value: "bocpd", label: "BOCPD · change alarm" },
]);

export const REGIME_NAMES = Object.freeze(STATES);
